// src/components/AvatarMenu.js
import React, { useState, useEffect, useRef, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { useSpeech } from '../context/SpeechContext';
import { useAudio } from '../context/AudioContext';
import useCircadianState from '../hooks/useCircadianState';
import './AvatarMenu.css';
import { useI18n } from '../i18n';
import AgentWidget from './AgentWidget';
import GlobalMusicPlayer from './GlobalMusicPlayer';

/**
 * AvatarMenu:
 * - Muestra avatar en esquina izquierda.
 * - Dropdown incluye:
 *   - "Acerca / Léeme"
 *   - "Cerrar Sesión"
 *   - Footer: Toggle Modo Claro/Oscuro y Settings (Gear)
 */
function AvatarMenu({
  logout,
  user: propUser = {},
  updateUserAvatar = () => {},
  hasBackground = false,
  settings,
  onCycleBackgroundMode = () => {}
}) {
  const { user: contextUser, refreshUser } = useContext(AuthContext);
  const { t } = useI18n();
  const user = contextUser || propUser;

  const avatars = ['🐱', '🐶', '🦊', '🐻', '🐼', '🐸', '🦁', '🐨', '🐯'];
  const [avatar, setAvatar] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const menuRef = useRef();

  const { currentEnergy, currentState } = useCircadianState();

  // Integrated hooks for mobile replicas visibility
  const { isListening } = useSpeech();
  const { isSpeaking, isPlaying: audioPlaying } = useAudio();
  const isAudioActive = isSpeaking || audioPlaying;
  // We'll also consider thinking state if we can access it, but for now we use these.
  const isAgentActuallyActive = isAudioActive || isListening;

  // Theme state
  const [isDark, setIsDark] = useState(true);

  useEffect(() => {
    if (refreshUser) refreshUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const effectiveAvatar = user?.avatar || user?.avatar_url;
    if (user && effectiveAvatar) {
      setAvatar(effectiveAvatar);
    } else {
      if (user && user.id) {
          const randomAvatar = avatars[Math.floor(Math.random() * avatars.length)];
          setAvatar(randomAvatar);
          try { updateUserAvatar(randomAvatar); } catch (_) {}
      }
    }

    // Check initial theme
    const checkTheme = () => {
        const theme = document.documentElement.getAttribute('data-theme');
        setIsDark(theme !== 'light');
    };
    checkTheme();

    // Observer for external changes (e.g. from Ajustes page)
    const observer = new MutationObserver(checkTheme);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => observer.disconnect();
    // eslint-disable-next-line
  }, [user?.avatar, user?.avatar_url, user?.id]);

  const toggleMenu = () => setMenuOpen(prev => !prev);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleNavigate = (path) => {
    navigate(path);
    setMenuOpen(false);
  };

  const handleLogout = () => {
    if (typeof logout === 'function') logout();
    navigate('/login');
    setMenuOpen(false);
  };

  const toggleTheme = () => {
      const newIsDark = !isDark;
      setIsDark(newIsDark);
      document.documentElement.setAttribute('data-theme', newIsDark ? 'dark' : 'light');
      localStorage.setItem('darkMode', newIsDark ? 'true' : 'false');
  };

  const backgroundStateLabel = settings?.enabled
    ? (settings?.mode === 'random' ? 'BG AUTO' : 'BG ON')
    : 'BG OFF';

  const isUrl = (str) => {
    if (typeof str !== 'string') return false;
    const s = str.trim();
    return /^(https?:\/\/|blob:)/i.test(s);
  };

  const getEnergyColor = (energy) => {
    if (energy > 70) return '#4caf50'; // Alert (Green)
    if (energy > 30) return '#ffeb3b'; // Tired (Yellow)
    return '#f44336'; // Sleeping/Critical (Red)
  };

  return (
    <div className="avatar-menu-container" ref={menuRef}>
      <button className="avatar-button" onClick={toggleMenu} aria-label={t('nav.avatarMenu')} style={{ position: 'relative' }}>
        {/* Stamina Indicator Ring */}
        <svg className="stamina-ring" width="40" height="40">
          <circle
            cx="20"
            cy="20"
            r="18"
            fill="none"
            stroke="rgba(255,255,255,0.1)"
            strokeWidth="3"
          />
          <circle
            cx="20"
            cy="20"
            r="18"
            fill="none"
            stroke={getEnergyColor(currentEnergy)}
            strokeWidth="3"
            strokeDasharray={`${(currentEnergy / 100) * 113} 113`}
            strokeLinecap="round"
            transform="rotate(-90 20 20)"
            style={{ transition: 'stroke-dasharray 0.5s ease, stroke 0.5s ease' }}
          />
        </svg>
        {isUrl(avatar) ? (
            <img
                src={avatar.trim()}
                alt="Avatar"
                className="avatar-image"
                referrerPolicy="no-referrer"
                style={{ width: '32px', height: '32px', borderRadius: '50%', objectFit: 'cover' }}
            />
        ) : (
            <span className="avatar-icon" aria-hidden>{avatar || '🙂'}</span>
        )}
      </button>
      {menuOpen && (
        <div className="avatar-dropdown" role="menu">
          <div className="avatar-dropdown-header">
             <span className="dropdown-display-name">
                {user?.displayAlias || user?.displayName || user?.username}
                {user?.dynamicNameEnabled && user?.learnedNickname && user?.learnedNickname !== 'null' && !user?.displayName && (
                  <span title={t('nav.aiNickname')} className="ai-name-badge"> ✨</span>
                )}
             </span>
             <span className="dropdown-username">
                @{user?.username}
             </span>
             <div className="stamina-status-bar">
                <span className="stamina-label">{t('nav.aiEnergy', { value: Number(currentEnergy).toFixed(1) })}</span>
                <span className={`stamina-badge ${(currentState || 'ALERT').toLowerCase()}`}>{currentState || 'ALERT'}</span>
             </div>
          </div>
          <div style={{ borderTop: '1px solid var(--border-color, rgba(255,255,255,0.1))', margin: '5px 0' }} />

          <button onClick={() => handleNavigate('/acerca')}>{t('common.about')}</button>

          <button onClick={() => handleNavigate('/contact')}>{t('contact.title')}</button>

          <div style={{ borderTop: '1px solid var(--border-color, rgba(255,255,255,0.1))', margin: '5px 0' }} />

          <button className="avatar-logout" onClick={handleLogout}>{t('common.logOut')}</button>

          {hasBackground && (
            <button
              className={`avatar-bg-toggle-btn ${settings?.enabled ? 'enabled' : 'disabled'} ${settings?.mode === 'random' ? 'random' : ''}`}
              onClick={onCycleBackgroundMode}
              aria-label={t('nav.globalBackground')}
              title={
                settings?.enabled
                  ? (settings?.mode === 'random' ? t('nav.globalBackgroundAuto') : t('nav.globalBackgroundOn'))
                  : t('nav.globalBackgroundOff')
              }
            >
              {backgroundStateLabel}
            </button>
          )}

          <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'center', gap: '15px' }}>
             {/* Theme Toggle */}
             <button
                onClick={toggleTheme}
                className="theme-toggle-icon-btn"
                title={isDark ? t('nav.lightMode') : t('nav.darkMode')}
             >
                 {isDark ? '🌙' : '☀️'}
             </button>

             {/* Settings Gear */}
             <button
                onClick={() => handleNavigate('/ajustes')}
                className="theme-toggle-icon-btn"
                title={t('common.settings')}
             >
                 ⚙️
             </button>
          </div>

          <div className="mobile-controls-replica">
            {!isAgentActuallyActive && (
              <div className="replica-item">
                <AgentWidget inMenu={true} />
              </div>
            )}

            <div className="replica-item">
              <GlobalMusicPlayer inMenu={true} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AvatarMenu;
